import { ArrowDropDown } from "@mui/icons-material";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Farm,
  selectAllFarms,
  selectCurrentFarm,
  switchFarm,
  useAppDispatch,
  useAppSelector,
} from "model";
import { MouseEvent, useState } from "react";
import { useHistory } from "react-router-dom";
import styled, { css } from "styled-components";
import { mixpanel } from "../../common/analytics";
import { useTranslation } from "../../common/locales";

const fontVariants = [
  "button",
  "caption",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "inherit",
  "subtitle1",
  "subtitle2",
  "body1",
  "body2",
  "overline",
  undefined,
] as const;
export type FontVariant = typeof fontVariants[number];

// Brand master colors for automatic site assignment
const siteColors = [
  "#4CAF50", // Light green
  "#9C27B0", // Light purple
  "#2E7D32", // Dark green
  "#FF9800", // Orange
  "#2196F3", // Blue
  "#E91E63", // Pink
  "#607D8B", // Blue grey
  "#795548", // Brown
];

export default function FarmSwitch(params: {
  switchToFarmView: boolean;
  fontVariant: FontVariant;
  fontColor: string;
  fontWeight?: number;
  showBackground?: boolean;
  centerText?: boolean;
  getSingeFarmName?: boolean;
}) {
  const {
    switchToFarmView,
    fontVariant,
    fontColor,
    fontWeight,
    showBackground = true,
    centerText = false,
  } = params;
  const [changeFarmAnchor, setChangeFarmAnchor] =
    useState<HTMLElement | null>();
  const [isSwitching, setIsSwitching] = useState(false);

  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const user = useAppSelector((state) => state.user.details);
  const userFarms = useAppSelector(selectAllFarms);
  const currentFarm = useAppSelector(selectCurrentFarm);

  const dispatch = useAppDispatch();

  const handleProfileClick = () => {
    history.push("/profile");
  };

  // When there's only one farm, show just the profile button
  if (userFarms.length === 1) {
    return (
      <>
        {isMobile && (
          <FarmSwitchContainer>
            <Box
              onClick={handleProfileClick}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                cursor: "pointer",
                ml: "auto",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  backgroundColor: "#f5f5f5",
                },
              }}
              aria-label="profile"
            >
              <AccountBoxIcon sx={{ fontSize: 28, color: fontColor }} />
            </Box>
          </FarmSwitchContainer>
        )}
      </>
    );
  }

  const byName = (l: Farm, b: Farm) => l.name.localeCompare(b.name);

  // Show own farms first (if there are any)
  const ownFarms = userFarms.filter((f) => f.owner === user?.guid).sort(byName);
  const consultingFarms = userFarms
    .filter((f) => f.owner !== user?.guid)
    .sort(byName);

  const userFarmsGrouped = [...ownFarms, ...consultingFarms];

  // Get color for farm based on index
  const getFarmColor = (farmIndex: number) => {
    return siteColors[farmIndex % siteColors.length];
  };

  // Get current farm index for color
  const currentFarmIndex = currentFarm
    ? userFarmsGrouped.findIndex((f) => f.guid === currentFarm.guid)
    : 0;

  return (
    <>
      <FarmSwitchContainer>
        <FarmSwitchButton
          onClick={openFarmSelector}
          disabled={isSwitching}
          $showBackground={isMobile ? true : showBackground}
          $centerText={centerText}
          $isMobile={isMobile}
        >
          {isSwitching ? (
            <CircularProgress size={16} sx={{ color: fontColor }} />
          ) : (
            <ArrowDropDown sx={{ color: fontColor }} />
          )}
          {isMobile && (
            <SiteColorIndicator color={getFarmColor(currentFarmIndex)} />
          )}
          {!isMobile && showBackground && (
            <SiteColorIndicator color={getFarmColor(currentFarmIndex)} />
          )}
          <FarmSwitchContent $centerText={centerText} $isMobile={isMobile}>
            <Typography
              variant={fontVariant}
              sx={{
                color: fontColor,
                fontWeight: fontWeight,
                fontSize: "1.1rem",
              }}
            >
              {currentFarm?.name}
            </Typography>
          </FarmSwitchContent>
          {isMobile && (
            <Box
              component="div"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                handleProfileClick();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                ml: "auto",
                cursor: "pointer",
                borderRadius: "50%",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              aria-label="profile"
            >
              <AccountBoxIcon sx={{ fontSize: 28, color: fontColor }} />
            </Box>
          )}
        </FarmSwitchButton>
      </FarmSwitchContainer>

      <Menu
        id="farm-selector-menu"
        anchorEl={changeFarmAnchor}
        keepMounted
        open={Boolean(changeFarmAnchor)}
        onClose={() => setChangeFarmAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: changeFarmAnchor ? changeFarmAnchor.offsetWidth : "auto",
            mt: 1,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            borderRadius: 2,
          },
        }}
      >
        {/* Add new Farm option - always at the top */}
        <MenuItem
          onClick={handleAddNewFarm}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            py: 1.5,
            px: 2,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <AddCircleIcon
            sx={{ fontSize: 24, color: theme.palette.primary.main }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t("farmSwitch.addNewFarm")}
          </Typography>
        </MenuItem>
        <Divider />
        {/* Header text for the dropdown */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
            }}
          >
            {t("farmSwitch.viewDataForDifferentSite")}
          </Typography>
        </Box>
        {userFarmsGrouped.map((farm) => {
          // Get the original index of this farm in the full grouped array
          const originalIndex = userFarmsGrouped.findIndex(
            (f) => f.guid === farm.guid
          );
          const isCurrentFarm = farm.guid === currentFarm?.guid;

          return (
            <MenuItem
              onClick={() => onSelectFarm(farm.guid)}
              key={farm.guid}
              disabled={isCurrentFarm || isSwitching}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 1.5,
                px: 2,
                opacity: isCurrentFarm ? 0.6 : 1,
                "&:hover": {
                  backgroundColor: isCurrentFarm
                    ? "transparent"
                    : theme.palette.action.hover,
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                },
              }}
            >
              <SiteColorIndicator color={getFarmColor(originalIndex)} />
              <Typography variant="body2">
                {farm.name}
                {isCurrentFarm && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ml: 1,
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    {t("farmSwitch.current")}
                  </Typography>
                )}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );

  function onSelectFarm(farmId: string) {
    if (farmId === currentFarm?.guid) return;

    setIsSwitching(true);
    setChangeFarmAnchor(null);

    try {
      void dispatch(switchFarm(farmId));
      mixpanel.track("Farm changed");

      if (switchToFarmView) {
        history.replace(`/farm/${farmId}`);
      }
    } catch (error) {
      console.error("Failed to switch farm:", error);
    } finally {
      // Add a small delay to show loading state
      setTimeout(() => setIsSwitching(false), 500);
    }
  }

  function openFarmSelector(event: MouseEvent<HTMLButtonElement>) {
    if (isSwitching) return;
    setChangeFarmAnchor(event.currentTarget);
  }

  function handleAddNewFarm() {
    setChangeFarmAnchor(null);
    mixpanel.track("FarmAdd started");
    history.push("/add-farm/enter-name");
  }
}

const Headline = styled(Typography)<{ $singlefarm?: boolean }>`
  text-overflow: ellipsis;
  overflow-x: hidden;
  text-align: left;
  ${({ $singlefarm, theme }) =>
    $singlefarm &&
    css`
      margin: ${theme.spacing(2)};
      margin-left: 0;
    `}
`;

const FarmSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const FarmSwitchButton = styled(ButtonBase)<{
  $showBackground?: boolean;
  $centerText?: boolean;
  $isMobile?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: ${({ $showBackground, $isMobile }) =>
    $showBackground || $isMobile ? "white" : "transparent"};
  border-radius: ${({ $isMobile }) => ($isMobile ? "12px" : "8px")};
  border: ${({ $showBackground, $isMobile }) =>
    $showBackground || $isMobile ? "none" : "none"};
  width: 100%;
  justify-content: ${({ $centerText }) =>
    $centerText ? "center" : "flex-start"};
  transition: all 0.2s ease;
  box-shadow: ${({ $isMobile }) =>
    $isMobile ? "0 2px 8px rgba(0, 0, 0, 0.1)" : "none"};

  &:hover:not(:disabled) {
    background-color: ${({ $showBackground, $isMobile }) =>
      $showBackground || $isMobile ? "#f5f5f5" : "rgba(255, 255, 255, 0.1)"};
    box-shadow: ${({ $isMobile }) =>
      $isMobile ? "0 4px 12px rgba(0, 0, 0, 0.15)" : "none"};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SiteColorIndicator = styled.div<{ color: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const FarmSwitchContent = styled.div<{
  $centerText?: boolean;
  $isMobile?: boolean;
}>`
  display: flex;
  align-items: ${({ $centerText }) => ($centerText ? "center" : "flex-start")};
  ${({ $centerText, $isMobile }) =>
    $centerText !== true &&
    css`
      flex-direction: column;
      flex: ${$isMobile ? "0 1 auto" : "1"};
    `}
  min-width: 0;
`;
